// @ts-check
/* eslint no-param-reassign: ["error", { "props": false }] */

/**
* A field, displayable on the page
* @typedef {Object} Field
* @property {string} group - Indicates whether the Courage component is present.
*/

import Vue from 'vue'
import Vuex from 'vuex'
import i18n from '../locales/'
import axios from 'axios'
import amplitude from 'amplitude-js'
import setWith from 'lodash.setwith'

Vue.use(Vuex)

const store = new Vuex.Store({
  strict: true,
  state: {
    logoURL: '',
    hasBranding: false,
    config: {},
    clientInfo: {},
    envelope: {
      documents: [],
      title: null,
      id: null
    },
    parties: [],
    colours: {},
    currentDocument: 0, // index
    activeTabId: 0, // index
    activeFieldId: null,
    sequenceGuideNumber: 1, // number
    signatureToUse: null,
    latestSignature: null,
    signatures: {
      drawn: null,
      typed: null,
      uploaded: null,
      signatureData: null,
      encodedJPG: null
    },
    doneFields: {}, // Fields which have been filled in. Keyed under document id
    isMobile: false,
    uploaders: {},
    passwordRequired: false,
    requestedEnvelope: {
      envelope_fingerprint: null,
      signature_fingerprint: null,
      preview_encrypt: null,
      isPreview: null,
      is_new_encrypt: null,
      widgetCode: null
    },
    widget: {}
  },
  mutations: {
    setWidget: (state, { widget, mergeFields }) => {
      state.widget = widget

      if (mergeFields) {
        state.widget.mergeFields = Object.keys(mergeFields).map((key) => { // eslint-disable-line arrow-body-style
          return {
            fieldName: key,
            value: mergeFields[key]
          }
        })
      }

      Object.values(state.widget.fields).forEach((page) => {
        page.forEach((field) => {
          field.content = ''
          if (field.merge_field) {
            const mf = state.widget.mergeFields.find(mergeField => mergeField.fieldName === field.merge_field)
            if (mf && mf.value) {
              field.content = mf.value
              field.prefilled = true
            }
            field.is_merge_field = true
          }
        })
      })

      state.config = widget.config // Set company config
      if (widget.config.company_signature_default === 'upload') {
        state.signatureToUse = 'uploaded'
      } else {
        state.signatureToUse = widget.config.company_signature_default
      }

      state.envelope.title = widget.title // Set envelope details
      state.envelope.id = widget.id
      state.envelope.pages = widget.pages

      state.colours = widget.colours // Set colours

      state.logoURL = widget.logo // Set branding
      state.hasBranding = widget.hasBranding // Set branding
    },
    setEnvelopeRegular: (state, { data }) => {
      state.config = data.config // Set company config
      if (data.config.company_signature_default === 'upload') {
        state.signatureToUse = 'uploaded'
      } else {
        state.signatureToUse = data.config.company_signature_default
      }

      state.envelope.title = data.envelope_title // Set envelope details
      state.envelope.id = data.envelope_id

      state.colours = data.colours // Set colours

      state.logoURL = data.logoURL // Set branding
      state.hasBranding = data.hasBranding // Set branding

      data.parties.forEach((party) => { // Set parties
        state.parties.push(party)
      })

      state.clientInfo = data.client // Set client info

      data.documents.forEach((doc) => { // Set documents
        state.envelope.documents.push(doc)
      })

      state.envelope.documents.forEach((document) => {
        Object.values(document.fields).forEach((page) => {
          page.forEach((field) => {
            if (field.merge_field && field.content) field.prefilled = true
          })
        })
      })
    },
    setActiveField: (state, { activeFieldId }) => {
      state.activeFieldId = activeFieldId
    },
    setCurrentFieldValue: (state, { field, val, property, isWidget }) => {
      const updatedFields = isWidget ? state.widget.fields : state.envelope.documents[field.docIndex].fields

      updatedFields[field.page][field.index][property] = val
      setWith(updatedFields, `${field.page}.${field.index}.${property}`, val, function (nsValue, key, nsObject) {
        return Vue.set(nsObject, key, nsValue)
      })
    },
    removePrevFieldSignatures: (state, { field, property }) => {
      switch (property) {
        case 'drawn_json':
          field.content = null
          field.upload = null
          break
        case 'content':
          field.drawn_json = null
          field.upload = null
          break
        default:
          field.drawn_json = null
          field.content = null
      }
    },
    setActiveTab: (state, { activeTabId, tabProperty }) => {
      state.activeTabId = activeTabId
      state.signatureToUse = tabProperty
    },
    // setSignature: (state, { signature, type, signatureData, encodedJPG }) => {
    //   state.signatures[type] = signature
    //   if (signatureData) {
    //     state.signatures.signatureData = signatureData
    //     state.signatures.encodedJPG = encodedJPG
    //   }
    // },
    setLatestSignature: (state, field) => {
      const {
        content,
        drawn_json, // eslint-disable-line camelcase
        upload
      } = field

      state.latestSignature = {
        content,
        drawn_json,
        upload
      }
    },
    setMobile: (state, { isMobile }) => {
      state.isMobile = isMobile
    },
    setUploader: (state, { uploader, fieldId }) => {
      state.uploaders[fieldId] = uploader
    },
    /**
     * Done Fields are keyed under the document they're a part of
     */
    addDoneField: (state, { field, fingerprint }) => {
      // If no document key, add it
      const hasFingerprint = Object.prototype.hasOwnProperty.call(state.doneFields, fingerprint)
      if (!hasFingerprint) {
        Vue.set(state.doneFields, fingerprint, []) // Use explicit setter as it's a new reactive prop
      }
      const isFieldDone = state.doneFields[fingerprint].filter(
        doneField => doneField.id === field.id).length > 0
      if (!isFieldDone) {
        state.doneFields[fingerprint].push(field)
      }
    },
    removeDoneField: (state, { field, fingerprint }) => {
      let index = 0
      let documentFingerprint = fingerprint
      if (!documentFingerprint) {
        const current = state.currentDocument
        documentFingerprint = state.envelope.documents[current].fingerprint
      }

      if (!state.doneFields[documentFingerprint]) return // Stops empty done array error below

      state.doneFields[documentFingerprint].filter((doneField, fieldIndex) => {
        if (doneField.id === field.id) {
          index = fieldIndex
        }
        return false
      })
      state.doneFields[documentFingerprint].splice(index, 1)
    },
    incrementSequence: (state, { newSequenceNumber }) => {
      state.sequenceGuideNumber = newSequenceNumber
    },
    nextDocument: (state, { index }) => {
      state.currentDocument = index
    },
    setRequestedEnvelope: (state, requestedEnvelope) => {
      state.requestedEnvelope = requestedEnvelope
    }
  },
  actions: {
    getEnvelope: ({ getters, commit }, { query, password }) => {
      const requestedEnvelope = getters.requestedEnvelope

      if (requestedEnvelope.widgetCode) {
        return axios.get(`${process.env.VUE_APP_API_URL}/ajax/envelope-widget-data.php?widget_id=${requestedEnvelope.widgetCode}`).then((response) => {
          commit('setWidget', { widget: response.data, mergeFields: query })
          amplitude.getInstance().logEvent('envelope_loaded_widget')
          return response
        })
      } else if (requestedEnvelope.preview_encrypt) {
        return axios.get(`${process.env.VUE_APP_API_URL}/ajax/signing-page-preview.php`, {
          params: requestedEnvelope
        }).then(data => axios.get(`${process.env.VUE_APP_API2_URL}/data/${data.data.envelope_fingerprint}/${data.data.signature_fingerprint}`, {
          params: {
            'passphrase': password
          }
        }).then(response => response).then((response) => {
          commit('setEnvelopeRegular', { data: response.data })

          return response
        }))
      }

      return axios.get(`${process.env.VUE_APP_API2_URL}/data/${requestedEnvelope.envelope_fingerprint}/${requestedEnvelope.signature_fingerprint}`, {
        params: {
          'passphrase': password
        }
      }).then((response) => {
        if (response.data.error) {
          throw response
        }
        commit('setEnvelopeRegular', { data: response.data })
        amplitude.getInstance().setUserId(response.data.client.client_id)
        amplitude.getInstance().logEvent('envelope_loaded_regular')
        return response
      })
    },
    setCurrentFieldVal: ({ commit, getters }, { val, id, property }) => {
      const field = getters.getField(id, false)
      const isWidget = getters.isWidget
      commit('setCurrentFieldValue', { field, val, property, isWidget })

      if (field.type === 'signature') commit('setLatestSignature', field)
    },
    removePrevFieldSignatures: ({ commit, getters }, { id, property }) => {
      const field = getters.getField(id, false)
      commit('removePrevFieldSignatures', { field, property })
    },
    addDoneField: ({ commit, getters }, { field }) => {
      const fingerprint = getters.currentDocumentFingerPrint
      const isFieldGroup = field.group !== ''

      if (field.required === 'true') {
        if (isFieldGroup) {
          const allFields = getters.requiredFieldsForParty
          const fieldsInGroup = allFields.filter(groupField => groupField.group === field.group)
          fieldsInGroup.forEach((item) => {
            commit('addDoneField', { field: item, fingerprint })
          })
        } else if (field.type === 'checkbox' || field.type === 'dropdown') {
          if (field.content === '0') {
            commit('removeDoneField', { field, fingerprint })
          } else {
            commit('addDoneField', { field, fingerprint })
          }
        } else {
          const doneFields = getters.doneFields(fingerprint)
          if (!(doneFields && doneFields.find((doneField) => field.id === doneField.id))) commit('addDoneField', { field, fingerprint })
        }
      }
    },
    setUploader: ({ commit }, { uploader, fieldId }) => {
      commit('setUploader', { uploader, fieldId })
    },
    nextDocument: ({ commit, getters, state }) => {
      let incompleteDoc = null

      state.envelope.documents.forEach((doc, docIndex) => {
        const fingerprint = doc.fingerprint
        const documentFields = getters.fieldsForDocument(docIndex)
        const doneFields = state.doneFields[fingerprint]

        documentFields.forEach((field) => {
          if (doneFields) {
            const isFieldDone = doneFields.filter(doneField => field.id === doneField.id).length > 0
            if (!isFieldDone && incompleteDoc === null) incompleteDoc = docIndex
          } else if (incompleteDoc === null) {
            incompleteDoc = docIndex
          }
        })
      })
      commit('nextDocument', { index: incompleteDoc })
    }
  },
  getters: {
    outstandingFieldsNo: (state, getters) => {
      const requiredFields = getters.removeFieldGroups(getters.requiredFieldsForParty)
      const doneFields = getters.removeFieldGroups(getters.doneEnvelopeFields)
      return requiredFields.length - doneFields.length
    },
    // Only show questions if company has allowed, and not widget
    canShowQuestions: (state, getters) => getters.config('company_questions_signing') === '1' && !getters.isWidget,
    envelopeDocuments: state => state.envelope.documents,
    latestSignature: state => state.latestSignature,
    currentDocument: (state) => {
      if (state.envelope.documents && state.envelope.documents.length > 0) return state.envelope.documents[state.currentDocument]

      const widget = store.state.widget
      widget.pages = parseInt(widget.pages, 10)
      return widget
    },
    isWidget: state => !!state.widget.fields,
    widgetRedirectUrl: state => state.widget.redirect_url,
    title: state => state.envelope.title,
    requestedEnvelope: state => state.requestedEnvelope,
    config: state => property => state.config[property],

    /**
     * Unsure what this function's intent is.
     * TODO refactor this
     * Returns items filtered in to two arrray [[],[]]
     * @param {...Field} fields - Array of items to filter and sort
     * @return [Array]
     */
    removeFieldGroups: () => (fields) => {
      const newFields = []
      let groupName
      const otherFields = fields.filter(field => field.group === '')
      const groups = fields
        .filter(field => field.group !== '')
        .sort((a, b) => {
          const groupA = a.group.toUpperCase()
          const groupB = b.group.toUpperCase()

          if (groupA < groupB) {
            return -1
          } else if (groupA > groupB) {
            return 1
          }

          return 0
        })

      groups.forEach((field) => {
        if (field.group !== groupName) newFields.push(field)
        groupName = field.group
      })

      return [...otherFields, ...newFields]
    },
    document: state => index => state.envelope.documents[index],
    /**
     * Get the current document fingerprint
     * @return [String,Boolean] Returns a string or boolean
     */
    currentDocumentFingerPrint: (state, getters) => {
      if (state.widget && state.widget.fingerprint) return state.widget.fingerprint
      const doc = getters.document(state.currentDocument)
      if (doc) return doc.fingerprint

      return false
    },
    /**
     * Returns the current party id if available, else undefined
     * @return [String,undefined]
     */
    currentPartyId: (state) => {
      let currentPartyId
      state.parties.forEach((party) => {
        if (party.isSigner) {
          currentPartyId = party.party_id
        }
      })
      return currentPartyId
    },
    /**
     * Get the current party from the store
     * @return {Array<Object>}
     */
    currentParty: (state, getters) => {
      const currentPartyId = getters.currentPartyId
      return state.parties.filter(party => currentPartyId === party.party_id)[0]
    },
    allFields: (state) => {
      let fields = []

      const extractFields = (docFields, docIndex) => {
        const extractedFields = []
        const documentFields = Object.values(docFields)
        const pages = Object.keys(docFields)
        documentFields.forEach((pageFields, page) => {
          pageFields.forEach((field, index) => { // TODO Set doc number
            field.docIndex = docIndex
            field.page = pages[page]
            field.index = index
            extractedFields.push(field)
          })
        })

        return extractedFields
      }

      if (state.widget.fields) {
        fields = extractFields(state.widget.fields)
      } else if (state.envelope && state.envelope.documents.length > 0) {
        state.envelope.documents.forEach((doc, docIndex) => {
          fields = fields.concat(extractFields(doc.fields, docIndex))
        })
      }

      // when merge fields come back, 2 always comeback one with value,
      // other without. This function sanitizes this bug
      fields = Object.values(fields.reduce((acc, cur) =>
        Object.assign(acc, { [cur.id]: cur }), {}
      ))

      return fields
    },
    getField: (state, getters) => (id, returnValue = true, property) => {
      let allFields = getters.allFields

      const matchedField = allFields.filter(field => parseInt(field.id, 10) === parseInt(id, 10))

      if (matchedField.length > 0) {
        if (returnValue) return matchedField[0][property]

        return matchedField[0]
      }

      return ''
    },
    fieldsForParty: (state, getters) => {
      const allFields = getters.allFields
      const currentPartyId = getters.currentPartyId

      return allFields.filter(field => field.party_id === currentPartyId)
    },
    requiredFieldsForParty: (state, getters) => {
      const fieldsForParty = getters.fieldsForParty

      return fieldsForParty.filter((field) => {
        const isRequired = field.required === 'true'
        return isRequired && !(field.is_merge_field && field.prefilled)
      })
    },
    fieldsForDocument: (state, getters) => (documentIndex = null) => {
      const currentDoc = documentIndex === null ? state.currentDocument : documentIndex
      const doc = state.widget.fields ? state.widget : state.envelope.documents[currentDoc]
      const fields = []

      if (doc) {
        const documentFields = Object.values(doc.fields)
        const currentPartyId = getters.currentPartyId
        documentFields.forEach((pageFields) => {
          pageFields.forEach((field) => {
            const isForCurrentParty = field.party_id === currentPartyId
            const isRequired = field.required === 'true'

            if (isForCurrentParty && isRequired && !field.prefilled) fields.push(field)
          })
        })
      }

      return fields
    },
    nextField: (state, getters) => () => {
      const fields = getters.fieldsForDocument()
      const fingerprint = getters.currentDocumentFingerPrint
      const nextField = []

      if (fields.length > 0) {
        fields.forEach((field) => {
          let isFieldDone = []

          if (state.doneFields[fingerprint]) {
            isFieldDone = state.doneFields[fingerprint].length > 0 ? state.doneFields[fingerprint].filter(doneField => field.id === doneField.id) : []
          }

          if (isFieldDone.length < 1 && nextField.length === 0) nextField.push(field)
        })

        return nextField[0]
      }

      return nextField
    },
    doneFields: state => (fingerprint) => {
      if (fingerprint) return state.doneFields[fingerprint]

      const doneFieldsKeys = Object.keys(state.doneFields)
      const doneFields = state.doneFields
      const fields = []
      doneFieldsKeys.forEach(fingerprintKey => fields.push(...doneFields[fingerprintKey]))

      return fields
    },
    doneEnvelopeFields: state => {
      const doneFieldsKeys = Object.keys(state.doneFields)
      const doneFields = state.doneFields
      const fields = []
      doneFieldsKeys.forEach(fingerprintKey => fields.push(...doneFields[fingerprintKey]))
      return fields
    },
    /**
     * Returns whether the current document within the Envelope is finished
     * @return {Boolean}
     */
    isDocumentDone: (state, getters) => {
      const fingerprint = getters.currentDocumentFingerPrint
      let fieldsForCurrentDoc = getters.fieldsForDocument()
      const hasFingerprint = Object.prototype.hasOwnProperty.call(state.doneFields, fingerprint)
      let doneFields

      if (fieldsForCurrentDoc.length === 0) return true

      if (hasFingerprint) {
        doneFields = getters.removeFieldGroups(getters.doneFields(fingerprint))
        fieldsForCurrentDoc = getters.removeFieldGroups(fieldsForCurrentDoc)
      } else {
        return false
      }

      if (fieldsForCurrentDoc.length !== doneFields.length) return false

      return true
    },
    currentSignature: state => state.signatures[state.signatureToUse],
    isCurrentSignatureImage: (state, getters) => {
      if (!getters.currentSignature) return false

      return getters.currentSignature.indexOf('data:image') !== -1
    },
    /**
     * @return {Number}
     */
    envelopeCompletionPercent: (state, getters) => {
      const allRequiredFields = getters.removeFieldGroups(getters.requiredFieldsForParty).length
      let doneRequiredFields = 0
      const doneFields = getters.removeFieldGroups(getters.doneEnvelopeFields)
      if (doneFields.length > 0) {
        doneFields.forEach(() => {
          doneRequiredFields += 1
        })
      }

      // Avoid /0 errors
      if (allRequiredFields === 0) {
        return 100
      } else if (doneRequiredFields === 0) {
        return 0
      }

      return Math.round((doneRequiredFields / allRequiredFields) * 100)
    },
    isEnvelopeComplete: (state, getters) => () => getters.envelopeCompletionPercent === 100,
    footerNextButtonText: (state, getters) => () => {
      const isEnvelopeComplete = getters.isEnvelopeComplete()
      const isDocumentDone = getters.isDocumentDone
      const nextField = getters.nextField()

      if (isEnvelopeComplete) {
        return state.config.company_consumer_regulations === '1' ? i18n.t('footer.order') : i18n.t('footer.submit')
      } else if (isDocumentDone) {
        return i18n.t('footer.nextDocument')
      } else if (nextField) {
        const hasTitle = nextField.title !== 'Untitled'
        const buttonText = hasTitle ? nextField.title : i18n.t(`fieldPopup.type.${nextField.type}`)
        return i18n.t('footer.nextField', { field: buttonText })
      }

      return ''
    }
  }
})

export default store
